import type { U3204ModelRef, U3204ProviderRef } from '../U3204Service';

export type U3204BuildMissionModelFooterLabelInput = {
  defaultProvider: U3204ProviderRef | null | undefined;
  defaultModel: U3204ModelRef | null | undefined;
  notConfiguredLabel: string;
  separator: string;
};

export type U3204MissionModelFooterTagKind = 'provider' | 'model';

export type U3204MissionModelFooterTag = {
  kind: U3204MissionModelFooterTagKind;
  label: string;
  title: string;
};

export type U3204MissionModelFooterDisplay =
  | { kind: 'notConfigured'; label: string }
  | { kind: 'tags'; tags: U3204MissionModelFooterTag[] };

export function u3204RefDisplayLabel(ref: U3204ProviderRef | U3204ModelRef | null | undefined): string | null {
  if (!ref) return null;
  const name = ref.name?.trim();
  if (name) return name;
  const code = ref.code?.trim();
  return code || null;
}

function u3204RefTitle(ref: U3204ProviderRef | U3204ModelRef, displayLabel: string): string {
  const code = ref.code?.trim();
  if (code && code !== displayLabel) {
    return `${displayLabel} (${code})`;
  }
  return displayLabel;
}

/**
 * Structure d'affichage tags fournisseur / modèle sous le composer (BL-0154).
 */
export function u3204BuildMissionModelFooterDisplay(
  input: Omit<U3204BuildMissionModelFooterLabelInput, 'separator'>,
): U3204MissionModelFooterDisplay {
  const providerLabel = u3204RefDisplayLabel(input.defaultProvider);
  const modelLabel = u3204RefDisplayLabel(input.defaultModel);

  if (!providerLabel && !modelLabel) {
    return { kind: 'notConfigured', label: input.notConfiguredLabel };
  }

  const tags: U3204MissionModelFooterTag[] = [];

  if (providerLabel && input.defaultProvider) {
    tags.push({
      kind: 'provider',
      label: providerLabel,
      title: u3204RefTitle(input.defaultProvider, providerLabel),
    });
  }

  if (modelLabel && input.defaultModel) {
    tags.push({
      kind: 'model',
      label: modelLabel,
      title: u3204RefTitle(input.defaultModel, modelLabel),
    });
  }

  if (tags.length === 0) {
    return { kind: 'notConfigured', label: input.notConfiguredLabel };
  }

  return { kind: 'tags', tags };
}

/**
 * Libellé texte compact (accessibilité, titre de ligne).
 */
export function u3204BuildMissionModelFooterLabel(input: U3204BuildMissionModelFooterLabelInput): string {
  const display = u3204BuildMissionModelFooterDisplay(input);

  if (display.kind === 'notConfigured') {
    return display.label;
  }

  return display.tags.map((tag) => tag.label).join(input.separator);
}
